import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar, BookOpen, ShieldCheck, Network, Stethoscope, Building2 } from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";
import { API } from "../apiBase";
const BLOG_SCHEMA = breadcrumb([
  { name: "Accueil", url: "/" },
  { name: "Blog", url: "/blog" },
]);

// Fetch helper with retry to absorb transient failures (Googlebot, slow Cloudflare hop, etc.)
async function fetchPostsWithRetry(retries = 2) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const { data } = await axios.get(`${API}/blog/posts`, { timeout: 8000 });
      return data;
    } catch (e) {
      lastErr = e;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
  }
  throw lastErr;
}

const TOPICS = [
  {
    icon: <Network size={18} />,
    title: "Interopérabilité & référentiels nationaux",
    desc: "INS qualifiée, DMP, Mon Espace Santé, MSSanté, Pro Santé Connect, e-CPS, FHIR : guides et retours terrain.",
    href: "/produit",
  },
  {
    icon: <ShieldCheck size={18} />,
    title: "Conformité HDS, RGPD & PGSSI-S",
    desc: "Hébergement de données de santé, sécurité, traçabilité, droits patient et obligations réglementaires.",
    href: "/conformite",
  },
  {
    icon: <Stethoscope size={18} />,
    title: "Parcours patient & coordination",
    desc: "Télésanté, téléconsultation, téléexpertise, télésuivi : architectures et bonnes pratiques de déploiement.",
    href: "/solutions",
  },
  {
    icon: <Building2 size={18} />,
    title: "Éditeurs, industriels, CPTS & GHT",
    desc: "Insights pour DSI hospitaliers, éditeurs de logiciels santé, industriels medtech et coordinateurs territoriaux.",
    href: "/solutions",
  },
];

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiFailed, setApiFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchPostsWithRetry(2)
      .then((data) => { if (alive) setPosts(Array.isArray(data) ? data : []); })
      .catch((e) => {
        console.error("Failed to load blog posts:", e);
        if (alive) setApiFailed(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const hasPosts = posts.length > 0;

  return (
    <div style={{ background: "#F8FAFC" }} data-testid="page-blog">
      <SEO
        title="Blog — actualités et insights e-santé"
        description="Articles MetaConnect sur l'e-santé, l'interopérabilité, les référentiels nationaux (INS, DMP, MSSanté, Pro Santé Connect) et la conformité HDS / RGPD."
        canonical="/blog"
        schema={BLOG_SCHEMA}
      />

      {/* Hero — split 50/50 navy + white */}
      <section className="relative pt-16" data-testid="blog-hero">
        <div className="grid lg:grid-cols-2 min-h-[440px]">
          {/* LEFT — Navy */}
          <div
            className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 flex flex-col justify-center"
            style={{ background: "#0F2B5B" }}
          >
            <div
              className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full"
              style={{
                background: "rgba(79,195,247,0.12)",
                border: "1px solid rgba(79,195,247,0.30)",
                color: "#4FC3F7",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Blog
            </div>
            <h1
              className="mt-6 font-display"
              style={{
                color: "#ffffff",
                fontSize: "clamp(32px, 4.2vw, 52px)",
                fontWeight: 600,
                lineHeight: 1.08,
                letterSpacing: "-0.018em",
              }}
            >
              Insights <span style={{ color: "#4FC3F7" }}>e-santé</span>.
            </h1>
            <p className="mt-6 max-w-md" style={{ color: "#93B4D4", fontSize: 15.5, lineHeight: 1.55 }}>
              Décryptages, guides pratiques et retours d'expérience sur l'interopérabilité,
              la conformité et les parcours numériques de soins. Le blog MetaConnect couvre les
              référentiels du numérique en santé (INS, DMP, MSSanté, Pro Santé Connect, HDS, RGPD).
            </p>
          </div>
          {/* RIGHT — White */}
          <div
            className="px-6 sm:px-10 lg:px-14 py-16 lg:py-20 flex flex-col justify-center"
            style={{ background: "#ffffff", borderLeft: "0.5px solid #E8EDF5" }}
          >
            <div className="flex items-center gap-2 mb-5">
              <span style={{ width: 3, height: 22, background: "#4FC3F7", borderRadius: 2 }} />
              <span style={{ fontSize: 11, color: "#4FC3F7", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
                Thématiques abordées
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {TOPICS.map((t) => (
                <Link
                  key={t.title}
                  to={t.href}
                  className="p-4 flex gap-3 items-start group transition-all hover:-translate-y-0.5"
                  style={{
                    background: "#F0F9FF",
                    border: "0.5px solid #BAE6FD",
                    borderRadius: 12,
                    textDecoration: "none",
                  }}
                >
                  <div
                    className="grid place-items-center h-10 w-10 rounded-lg shrink-0"
                    style={{ background: "#ffffff", border: "0.5px solid #BAE6FD", color: "#0F2B5B" }}
                  >
                    {t.icon}
                  </div>
                  <div>
                    <h2 className="font-display text-[14px] leading-snug" style={{ color: "#0F2B5B", fontWeight: 500 }}>{t.title}</h2>
                    <p className="mt-1 text-[12.5px] leading-relaxed" style={{ color: "#64748B" }}>{t.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles list — light */}
      <section className="py-12 lg:py-20" style={{ background: "#F8FAFC" }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-2 mb-6">
            <span style={{ width: 3, height: 22, background: "#4FC3F7", borderRadius: 2 }} />
            <span style={{ fontSize: 11, color: "#4FC3F7", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
              Tous les articles
            </span>
          </div>

          {loading && (
            <div className="text-[13px]" style={{ color: "#64748B" }} data-testid="blog-loading">Chargement…</div>
          )}

          {!loading && hasPosts && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="p-6 flex flex-col h-full group transition-all hover:-translate-y-0.5"
                  style={{
                    background: "#ffffff",
                    border: "0.5px solid #E8EDF5",
                    borderRadius: 12,
                    boxShadow: "0 1px 2px rgba(15,43,91,0.04)",
                    textDecoration: "none",
                  }}
                >
                  {p.cover_image && (
                    <div className="rounded-lg overflow-hidden -mx-2 -mt-2 mb-5 aspect-[16/9]">
                      <img src={p.cover_image} alt={p.title} loading="lazy"
                           className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(p.tags || []).slice(0, 2).map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full"
                        style={{ background: "#F0F9FF", color: "#4FC3F7", border: "0.5px solid #BAE6FD", fontWeight: 600 }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-display text-[19px] leading-snug" style={{ color: "#0F2B5B", fontWeight: 500 }}>{p.title}</h3>
                  {p.excerpt && (
                    <p className="mt-3 text-[13.5px] leading-relaxed line-clamp-3 flex-1" style={{ color: "#64748B" }}>
                      {p.excerpt}
                    </p>
                  )}
                  <div className="mt-5 flex items-center gap-3 text-[11px]" style={{ color: "#94A3B8" }}>
                    <span className="inline-flex items-center gap-1.5"><Calendar size={11} />
                      {p.published_at ? new Date(p.published_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : ""}
                    </span>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-[12px]" style={{ color: "#4FC3F7", fontWeight: 500 }}>
                    Lire l'article <ArrowRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          )}

          {!loading && !hasPosts && (
            <div
              className="p-12 text-center"
              data-testid="blog-empty"
              style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 12 }}
            >
              <BookOpen size={28} className="mx-auto" style={{ color: "#4FC3F7" }} />
              <h3 className="mt-5 font-display text-xl" style={{ color: "#0F2B5B", fontWeight: 500 }}>
                {apiFailed ? "Articles momentanément indisponibles" : "Articles à venir"}
              </h3>
              <p className="mt-2 text-[14px] max-w-md mx-auto" style={{ color: "#64748B" }}>
                {apiFailed
                  ? "Réessayez dans quelques instants. Vous pouvez aussi explorer le produit ou planifier une démo."
                  : "Nos premiers articles arrivent prochainement."}
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/produit"
                  className="inline-flex items-center"
                  style={{
                    background: "#F1F5F9", color: "#0F2B5B",
                    borderRadius: 999, padding: "10px 20px", fontSize: 12, fontWeight: 500,
                    textDecoration: "none",
                  }}
                >
                  Voir le produit
                </Link>
                <Link
                  to="/demo"
                  className="inline-flex items-center gap-1.5"
                  style={{
                    background: "#0F2B5B", color: "#ffffff",
                    borderRadius: 999, padding: "10px 20px", fontSize: 12, fontWeight: 500,
                    textDecoration: "none",
                    boxShadow: "0 8px 22px -10px rgba(15,43,91,0.45)",
                  }}
                >
                  Planifier une démo <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
